import { EQUIPMENT_SOURCE_LICENSE_MANIFEST } from "./sourceLicenseManifest";
import type {
  CuratedOemSeedRecord,
  EquipmentEvidenceSource,
  SeedCoverageSummary,
} from "./seedSchema";

export interface SeedValidationIssue {
  readonly code: string;
  readonly message: string;
  readonly recordId?: string;
  readonly sourceId?: string;
}

export interface SeedValidationResult {
  readonly valid: boolean;
  readonly errors: readonly SeedValidationIssue[];
  readonly warnings: readonly SeedValidationIssue[];
  readonly coverage: SeedCoverageSummary;
}

const countBy = <T>(
  values: readonly T[],
  key: (value: T) => string,
): Readonly<Record<string, number>> =>
  Object.fromEntries(
    [...values.reduce((counts, value) => {
      const bucket = key(value);
      counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
      return counts;
    }, new Map<string, number>())].sort(([left], [right]) =>
      left.localeCompare(right),
    ),
  );

export function summarizeSeedCoverage(
  records: readonly CuratedOemSeedRecord[],
): SeedCoverageSummary {
  return {
    total: records.length,
    manufacturers: new Set(records.map((record) => record.manufacturer)).size,
    categories: countBy(records, (record) => record.classification.kind),
    domains: countBy(records, (record) => record.classification.domain),
    statuses: countBy(records, (record) => record.catalogStatus),
    evidenceTiers: countBy(records, (record) => record.evidenceTier),
    recordsWithRegionEvidence: records.filter(
      (record) => record.regions.length > 0,
    ).length,
    recordsWithFrequencyEvidence: records.filter(
      (record) => record.frequencyHz.length > 0,
    ).length,
  };
}

const REQUIRED_KIND_MINIMUMS: Readonly<Record<string, number>> = {
  "gas-turbine": 30,
  "steam-turbine": 10,
  "reciprocating-engine": 20,
  "wind-turbine": 15,
  "solar-module": 8,
  "solar-inverter": 2,
  "hydro-turbine": 5,
  boiler: 2,
  "heat-recovery-steam-generator": 3,
  "organic-rankine-cycle": 3,
  "nuclear-reactor-module": 2,
  "fuel-cell": 2,
  "battery-container": 4,
  "marine-energy-converter": 2,
  pump: 1,
  "cooling-tower": 2,
  condenser: 2,
  "synchronous-generator": 1,
  transformer: 1,
  compressor: 4,
};

const pushError = (
  errors: SeedValidationIssue[],
  issue: SeedValidationIssue,
): void => {
  errors.push(issue);
};

export function validateCuratedOemSeed(
  records: readonly CuratedOemSeedRecord[],
  sources: readonly EquipmentEvidenceSource[] =
    EQUIPMENT_SOURCE_LICENSE_MANIFEST,
): SeedValidationResult {
  const errors: SeedValidationIssue[] = [];
  const warnings: SeedValidationIssue[] = [];
  const coverage = summarizeSeedCoverage(records);
  const sourceById = new Map(sources.map((source) => [source.id, source]));

  if (records.length < 150) {
    pushError(errors, {
      code: "MINIMUM_RECORD_COUNT",
      message: `Expected at least 150 curated OEM records; found ${records.length}.`,
    });
  }

  const seenIds = new Set<string>();
  const seenIdentities = new Set<string>();

  for (const record of records) {
    if (!/^oem-[a-z0-9]+(?:-[a-z0-9]+)*$/.test(record.id)) {
      pushError(errors, {
        code: "INVALID_RECORD_ID",
        message: `Record id is not a stable lower-kebab OEM id: ${record.id}`,
        recordId: record.id,
      });
    }
    if (seenIds.has(record.id)) {
      pushError(errors, {
        code: "DUPLICATE_RECORD_ID",
        message: `Duplicate record id: ${record.id}`,
        recordId: record.id,
      });
    }
    seenIds.add(record.id);

    const identityKey =
      `${record.manufacturer}\u0000${record.family}`.toLocaleLowerCase("en-US");
    if (seenIdentities.has(identityKey)) {
      pushError(errors, {
        code: "DUPLICATE_OEM_IDENTITY",
        message: `Duplicate manufacturer/family identity: ${record.manufacturer} ${record.family}`,
        recordId: record.id,
      });
    }
    seenIdentities.add(identityKey);

    if (!record.manufacturer.trim() || !record.family.trim()) {
      pushError(errors, {
        code: "EMPTY_IDENTITY",
        message: "Manufacturer and family are required.",
        recordId: record.id,
      });
    }

    if (record.sourceIds.length === 0) {
      pushError(errors, {
        code: "MISSING_SOURCE",
        message: "Every curated identity requires at least one evidence source.",
        recordId: record.id,
      });
    }

    const expectedUrls: string[] = [];
    for (const sourceId of record.sourceIds) {
      const source = sourceById.get(sourceId);
      if (!source) {
        pushError(errors, {
          code: "UNKNOWN_SOURCE",
          message: `Unknown source id: ${sourceId}`,
          recordId: record.id,
          sourceId,
        });
        continue;
      }
      expectedUrls.push(source.url);
      if (source.evidenceTier !== record.evidenceTier) {
        pushError(errors, {
          code: "EVIDENCE_TIER_MISMATCH",
          message: `Record evidence tier ${record.evidenceTier} does not match source ${sourceId} tier ${source.evidenceTier}.`,
          recordId: record.id,
          sourceId,
        });
      }
    }
    if (
      JSON.stringify(record.sourceUrls) !== JSON.stringify(expectedUrls)
    ) {
      pushError(errors, {
        code: "SOURCE_URL_MISMATCH",
        message: "Source URLs must resolve deterministically from sourceIds.",
        recordId: record.id,
      });
    }

    if (record.dataGaps.length === 0) {
      pushError(errors, {
        code: "MISSING_DATA_GAPS",
        message:
          "Sparse identity records must explicitly declare outstanding data gaps.",
        recordId: record.id,
      });
    }

    if (
      new Set(record.frequencyHz).size !== record.frequencyHz.length ||
      record.frequencyHz.some((frequency) => frequency !== 50 && frequency !== 60)
    ) {
      pushError(errors, {
        code: "INVALID_FREQUENCY",
        message: "Frequency evidence must contain unique 50/60 Hz values only.",
        recordId: record.id,
      });
    }

    if (
      record.catalogStatus === "precommercial" &&
      ![
        "licensed-precommercial",
        "demonstrator",
        "under-construction",
        "announced",
        "status-unconfirmed",
      ].includes(record.lifecycleStatus)
    ) {
      pushError(errors, {
        code: "PRECOMMERCIAL_STATUS_CONFLICT",
        message:
          "A precommercial catalog record cannot claim a commercial lifecycle.",
        recordId: record.id,
      });
    }

    if (
      record.commercialMaturity === "commercial-operating" &&
      record.lifecycleStatus !== "commercial-current"
    ) {
      pushError(errors, {
        code: "MATURITY_STATUS_CONFLICT",
        message:
          "Commercial-operating maturity requires a commercial-current lifecycle.",
        recordId: record.id,
      });
    }
  }

  for (const source of sources) {
    if (!source.url.startsWith("https://")) {
      pushError(errors, {
        code: "INSECURE_SOURCE_URL",
        message: `Source URL must use HTTPS: ${source.url}`,
        sourceId: source.id,
      });
    }
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(source.lastVerifiedOn) ||
      !/^\d{4}-\d{2}-\d{2}$/.test(source.nextReviewOn)
    ) {
      pushError(errors, {
        code: "INVALID_REVIEW_DATE",
        message: "Source review dates must use YYYY-MM-DD.",
        sourceId: source.id,
      });
    }
    if (
      !source.rights.allowedUse.trim() ||
      !source.rights.prohibitedUse.trim() ||
      !source.rights.licenseOrTerms.trim()
    ) {
      pushError(errors, {
        code: "INCOMPLETE_RIGHTS_GOVERNANCE",
        message: "Every source requires explicit rights and use policy.",
        sourceId: source.id,
      });
    }
  }

  for (const [kind, minimum] of Object.entries(REQUIRED_KIND_MINIMUMS)) {
    const actual = coverage.categories[kind] ?? 0;
    if (actual < minimum) {
      pushError(errors, {
        code: "INSUFFICIENT_KIND_COVERAGE",
        message: `Expected at least ${minimum} ${kind} records; found ${actual}.`,
      });
    }
  }

  if (coverage.recordsWithRegionEvidence === 0) {
    warnings.push({
      code: "NO_REGION_EVIDENCE",
      message: "No product records contain normalized region evidence.",
    });
  }
  if (coverage.recordsWithFrequencyEvidence === 0) {
    warnings.push({
      code: "NO_FREQUENCY_EVIDENCE",
      message: "No product records contain normalized frequency evidence.",
    });
  }

  return {
    valid: errors.length === 0,
    errors: errors.sort((left, right) =>
      `${left.code}:${left.recordId ?? ""}:${left.sourceId ?? ""}`.localeCompare(
        `${right.code}:${right.recordId ?? ""}:${right.sourceId ?? ""}`,
      ),
    ),
    warnings: warnings.sort((left, right) =>
      left.code.localeCompare(right.code),
    ),
    coverage,
  };
}

export function assertValidCuratedOemSeed(
  records: readonly CuratedOemSeedRecord[],
  sources: readonly EquipmentEvidenceSource[] =
    EQUIPMENT_SOURCE_LICENSE_MANIFEST,
): void {
  const result = validateCuratedOemSeed(records, sources);
  if (!result.valid) {
    const details = result.errors
      .map(
        (issue) =>
          `${issue.code}${issue.recordId ? ` [${issue.recordId}]` : ""}: ${issue.message}`,
      )
      .join("\n");
    throw new Error(`Curated OEM seed validation failed:\n${details}`);
  }
}
